import { __extends } from "tslib";
import { AssetEvent } from '@awayjs/core';
import { ContextGLCompareMode, ImageBase } from '@awayjs/stage';
import { DefaultRenderer, DepthRenderer, DistanceRenderer, ImageTexture2D } from '@awayjs/renderer';
import { AmbientBasicMethod } from './methods/AmbientBasicMethod';
import { DiffuseBasicMethod } from './methods/DiffuseBasicMethod';
import { NormalBasicMethod } from './methods/NormalBasicMethod';
import { SpecularBasicMethod } from './methods/SpecularBasicMethod';
import { MaterialBase } from './MaterialBase';
import { MethodMaterialMode } from './MethodMaterialMode';
/**
 * MethodMaterial forms an abstract base class for the default shaded materials provided by Stage,
 * using material methods to define their appearance.
 */
var MethodMaterial = /** @class */ (function (_super) {
    __extends(MethodMaterial, _super);
    function MethodMaterial(imageColor, alpha) {
        if (imageColor === void 0) { imageColor = 0xFFFFFF; }
        if (alpha === void 0) { alpha = 1; }
        var _this = _super.call(this, imageColor, alpha) || this;
        _this._effectMethods = new Array();
        _this._enableLightFallOff = true;
        _this._specularLightSources = 0x01;
        _this._diffuseLightSources = 0x03;
        _this._ambientMethod = new AmbientBasicMethod();
        _this._diffuseMethod = new DiffuseBasicMethod();
        _this._normalMethod = new NormalBasicMethod();
        _this._specularMethod = new SpecularBasicMethod();
        _this._depthCompareMode = ContextGLCompareMode.LESS_EQUAL;
        _this._mode = MethodMaterialMode.SINGLE_PASS;
        //add default methods owners
        _this._ambientMethod.iAddOwner(_this);
        _this._diffuseMethod.iAddOwner(_this);
        _this._normalMethod.iAddOwner(_this);
        _this._specularMethod.iAddOwner(_this);
        _this._onLightChangeDelegate = function (event) { return _this.onLightsChange(event); };
        //set a texture if an image is present
        if (imageColor instanceof ImageBase)
            _this._ambientMethod.texture = new ImageTexture2D();
        return _this;
    }
    Object.defineProperty(MethodMaterial.prototype, "assetType", {
        /**
         *
         */
        get: function () {
            return MethodMaterial.assetType;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MethodMaterial.prototype, "lightPicker", {
        /**
         * The light picker used by the material to provide lights to the material if it supports lighting.
         *
         * @see LightPickerBase
         * @see StaticLightPicker
         */
        get: function () {
            return this._pLightPicker;
        },
        set: function (value) {
            if (this._pLightPicker == value)
                return;
            if (this._pLightPicker)
                this._pLightPicker.removeEventListener(AssetEvent.INVALIDATE, this._onLightChangeDelegate);
            this._pLightPicker = value;
            if (this._pLightPicker)
                this._pLightPicker.addEventListener(AssetEvent.INVALIDATE, this._onLightChangeDelegate);
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MethodMaterial.prototype, "enableLightFallOff", {
        /**
         * Whether or not to use fallOff and radius properties for lights. This can be used to improve performance and
         * compatibility for constrained mode.
         */
        get: function () {
            return this._enableLightFallOff;
        },
        set: function (value) {
            if (this._enableLightFallOff == value)
                return;
            this._enableLightFallOff = value;
            this.invalidatePasses();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MethodMaterial.prototype, "diffuseLightSources", {
        /**
         * Define which light source types to use for diffuse reflections. This allows choosing between regular lights
         * and/or light probes for diffuse reflections.
         *
         * @see away3d.materials.LightSources
         */
        get: function () {
            return this._diffuseLightSources;
        },
        set: function (value) {
            if (this._diffuseLightSources == value)
                return;
            this._diffuseLightSources = value;
            this.invalidatePasses();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MethodMaterial.prototype, "specularLightSources", {
        /**
         * Define which light source types to use for specular reflections. This allows choosing between regular lights
         * and/or light probes for specular reflections.
         *
         * @see away3d.materials.LightSources
         */
        get: function () {
            return this._specularLightSources;
        },
        set: function (value) {
            if (this._specularLightSources == value)
                return;
            this._specularLightSources = value;
            this.invalidatePasses();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MethodMaterial.prototype, "mode", {
        get: function () {
            return this._mode;
        },
        set: function (value) {
            if (this._mode == value)
                return;
            this._mode = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MethodMaterial.prototype, "depthCompareMode", {
        /**
         * The depth compare mode used to render the renderables using this material.
         *
         * @see away.stagegl.ContextGLCompareMode
         */
        get: function () {
            return this._depthCompareMode;
        },
        set: function (value) {
            if (this._depthCompareMode == value)
                return;
            this._depthCompareMode = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MethodMaterial.prototype, "ambientMethod", {
        /**
         * The method that provides the ambient lighting contribution. Defaults to AmbientBasicMethod.
         */
        get: function () {
            return this._ambientMethod;
        },
        set: function (value) {
            if (this._ambientMethod == value)
                return;
            if (this._ambientMethod)
                this._ambientMethod.iRemoveOwner(this);
            this._ambientMethod = value;
            if (this._ambientMethod)
                this._ambientMethod.iAddOwner(this);
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MethodMaterial.prototype, "shadowMethod", {
        /**
         * The method used to render shadows cast on this surface, or null if no shadows are to be rendered. Defaults to null.
         */
        get: function () {
            return this._shadowMethod;
        },
        set: function (value) {
            if (this._shadowMethod == value)
                return;
            if (this._shadowMethod)
                this._shadowMethod.iRemoveOwner(this);
            this._shadowMethod = value;
            if (this._shadowMethod)
                this._shadowMethod.iAddOwner(this);
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MethodMaterial.prototype, "diffuseMethod", {
        /**
         * The method that provides the diffuse lighting contribution. Defaults to DiffuseBasicMethod.
         */
        get: function () {
            return this._diffuseMethod;
        },
        set: function (value) {
            if (this._diffuseMethod == value)
                return;
            if (this._diffuseMethod)
                this._diffuseMethod.iRemoveOwner(this);
            this._diffuseMethod = value;
            if (this._diffuseMethod)
                this._diffuseMethod.iAddOwner(this);
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MethodMaterial.prototype, "specularMethod", {
        /**
         * The method that provides the specular lighting contribution. Defaults to SpecularBasicMethod.
         */
        get: function () {
            return this._specularMethod;
        },
        set: function (value) {
            if (this._specularMethod == value)
                return;
            if (this._specularMethod)
                this._specularMethod.iRemoveOwner(this);
            this._specularMethod = value;
            if (this._specularMethod)
                this._specularMethod.iAddOwner(this);
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MethodMaterial.prototype, "normalMethod", {
        /**
         * The method used to generate the per-pixel normals. Defaults to NormalBasicMethod.
         */
        get: function () {
            return this._normalMethod;
        },
        set: function (value) {
            if (this._normalMethod == value)
                return;
            if (this._normalMethod)
                this._normalMethod.iRemoveOwner(this);
            this._normalMethod = value;
            if (this._normalMethod)
                this._normalMethod.iAddOwner(this);
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MethodMaterial.prototype, "numEffectMethods", {
        get: function () {
            return this._effectMethods.length;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Appends an "effect" shading method to the shader. Effect methods are those that do not influence the lighting
     * but modulate the shaded colour, used for fog, outlines, etc. The method will be applied to the result of the
     * methods added prior.
     */
    MethodMaterial.prototype.addEffectMethod = function (method) {
        method.iAddOwner(this);
        this._effectMethods.push(method);
        this.invalidate();
    };
    /**
     * Returns the method added at the given index.
     * @param index The index of the method to retrieve.
     * @return The method at the given index.
     */
    MethodMaterial.prototype.getEffectMethodAt = function (index) {
        return this._effectMethods[index];
    };
    /**
     * Adds an effect method at the specified index amongst the methods already added to the material. Effect
     * methods are those that do not influence the lighting but modulate the shaded colour, used for fog, outlines,
     * etc. The method will be applied to the result of the methods with a lower index.
     */
    MethodMaterial.prototype.addEffectMethodAt = function (method, index) {
        method.iAddOwner(this);
        this._effectMethods.splice(index, 0, method);
        this.invalidate();
    };
    /**
     * Removes an effect method from the material.
     * @param method The method to be removed.
     */
    MethodMaterial.prototype.removeEffectMethod = function (method) {
        method.iRemoveOwner(this);
        this._effectMethods.splice(this._effectMethods.indexOf(method), 1);
        this.invalidate();
    };
    /**
     * Called when the light picker's configuration changed.
     */
    MethodMaterial.prototype.onLightsChange = function (event) {
        this.invalidate();
    };
    MethodMaterial.assetType = '[materials MethodMaterial]';
    return MethodMaterial;
}(MaterialBase));
export { MethodMaterial };
import { BlendMode } from '@awayjs/stage';
import { _Render_MaterialBase } from '@awayjs/renderer';
import { StaticLightPicker } from './lightpickers/StaticLightPicker';
import { MethodPassMode } from './passes/MethodPassMode';
import { MethodPass } from './passes/MethodPass';
import { _Render_DepthMaterial, _Render_DistanceMaterial } from './MaterialBase';
import { CacheRenderer } from '@awayjs/renderer';
/**
 * CompiledPass forms an abstract base class for the default compiled pass materials provided by Away3D,
 * using material methods to define their appearance.
 */
var _Render_MethodMaterial = /** @class */ (function (_super) {
    __extends(_Render_MethodMaterial, _super);
    /**
     * Creates a new CompiledPass object.
     *
     * @param material The material to which this pass belongs.
     */
    function _Render_MethodMaterial(material, renderElements) {
        var _this = _super.call(this, material, renderElements) || this;
        _this._methodMaterial = material;
        _this._renderElements = renderElements;
        return _this;
    }
    Object.defineProperty(_Render_MethodMaterial.prototype, "numLights", {
        /**
         * The maximum total number of lights provided by the light picker.
         */
        get: function () {
            return this._methodMaterial.lightPicker ? this._methodMaterial.lightPicker.numLightProbes + this._methodMaterial.lightPicker.numDirectionalLights + this._methodMaterial.lightPicker.numPointLights + this._methodMaterial.lightPicker.numCastingDirectionalLights + this._methodMaterial.lightPicker.numCastingPointLights : 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(_Render_MethodMaterial.prototype, "numNonCasters", {
        /**
         * The amount of lights that don't cast shadows.
         */
        get: function () {
            return this._methodMaterial.lightPicker ? this._methodMaterial.lightPicker.numLightProbes + this._methodMaterial.lightPicker.numDirectionalLights + this._methodMaterial.lightPicker.numPointLights : 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(_Render_MethodMaterial.prototype, "lightPicker", {
        get: function () {
            return this._methodMaterial.lightPicker;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(_Render_MethodMaterial.prototype, "enableLightFallOff", {
        /**
         * Whether or not to use fallOff and radius properties for lights. This can be used to improve performance and
         * compatibility for constrained mode.
         */
        get: function () {
            return this._methodMaterial.enableLightFallOff;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(_Render_MethodMaterial.prototype, "diffuseLightSources", {
        /**
         * Define which light source types to use for diffuse reflections. This allows choosing between regular lights
         * and/or light probes for diffuse reflections.
         *
         * @see away3d.materials.LightSources
         */
        get: function () {
            return this._methodMaterial.diffuseLightSources;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(_Render_MethodMaterial.prototype, "specularLightSources", {
        /**
         * Define which light source types to use for specular reflections. This allows choosing between regular lights
         * and/or light probes for specular reflections.
         *
         * @see away3d.materials.LightSources
         */
        get: function () {
            return this._methodMaterial.specularLightSources;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @inheritDoc
     */
    _Render_MethodMaterial.prototype._pUpdateRender = function () {
        _super.prototype._pUpdateRender.call(this);
        this.initPasses();
        this.setBlendAndCompareModes();
        this._pClearPasses();
        if (this._methodMaterial.mode == MethodMaterialMode.MULTI_PASS) {
            if (this._casterLightPass)
                this._pAddPass(this._casterLightPass);
            if (this._nonCasterLightPasses)
                for (var i = 0; i < this._nonCasterLightPasses.length; ++i)
                    this._pAddPass(this._nonCasterLightPasses[i]);
        }
        if (this._pass)
            this._pAddPass(this._pass);
    };
    /**
     * Initializes all the passes and their dependent passes.
     */
    _Render_MethodMaterial.prototype.initPasses = function () {
        // let the effects pass handle everything if there are no lights, when there are effect methods applied
        // after shading, or when the material mode is single pass.
        if (this.numLights == 0 || this._methodMaterial.numEffectMethods > 0 || this._methodMaterial.mode == MethodMaterialMode.SINGLE_PASS)
            this.initEffectPass();
        else if (this._pass)
            this.removeEffectPass();
        // only use a caster light pass if shadows need to be rendered
        if (this._methodMaterial.shadowMethod && this._methodMaterial.mode == MethodMaterialMode.MULTI_PASS)
            this.initCasterLightPass();
        else if (this._casterLightPass)
            this.removeCasterLightPass();
        // only use non caster light passes if there are lights that don't cast
        if (this.numNonCasters > 0 && this._methodMaterial.mode == MethodMaterialMode.MULTI_PASS)
            this.initNonCasterLightPasses();
        else if (this._nonCasterLightPasses)
            this.removeNonCasterLightPasses();
    };
    /**
     * Sets up the various blending modes for all screen passes, based on whether or not there are previous passes.
     */
    _Render_MethodMaterial.prototype.setBlendAndCompareModes = function () {
        var forceSeparateMVP = Boolean(this._casterLightPass || this._pass);
        // caster light pass is always first if it exists, hence it uses normal blending
        if (this._casterLightPass) {
            this._casterLightPass.forceSeparateMVP = forceSeparateMVP;
            this._casterLightPass.shader.setBlendMode(BlendMode.NORMAL);
            this._casterLightPass.shader.depthCompareMode = this._methodMaterial.depthCompareMode;
        }
        if (this._nonCasterLightPasses) {
            var firstAdditiveIndex = 0;
            // if there's no caster light pass, the first non caster light pass will be the first
            // and should use normal blending
            if (!this._casterLightPass) {
                this._nonCasterLightPasses[0].forceSeparateMVP = forceSeparateMVP;
                this._nonCasterLightPasses[0].shader.setBlendMode(BlendMode.NORMAL);
                this._nonCasterLightPasses[0].shader.depthCompareMode = this._methodMaterial.depthCompareMode;
                firstAdditiveIndex = 1;
            }
            // all lighting passes following the first light pass should use additive blending
            for (var i = firstAdditiveIndex; i < this._nonCasterLightPasses.length; ++i) {
                this._nonCasterLightPasses[i].forceSeparateMVP = forceSeparateMVP;
                this._nonCasterLightPasses[i].shader.setBlendMode(BlendMode.ADD);
                this._nonCasterLightPasses[i].shader.depthCompareMode = ContextGLCompareMode.LESS_EQUAL;
            }
        }
        if (this._casterLightPass || this._nonCasterLightPasses) {
            //cannot be blended by blendmode property if multipass enabled
            this.requiresBlending = false;
            // there are light passes, so this should be blended in
            if (this._pass) {
                this._pass.mode = MethodPassMode.EFFECTS;
                this._pass.forceSeparateMVP = forceSeparateMVP;
                this._pass.shader.depthCompareMode = ContextGLCompareMode.LESS_EQUAL;
                this._pass.shader.setBlendMode(BlendMode.LAYER);
            }
        }
        else if (this._pass) {
            this.requiresBlending = (this._methodMaterial.blendMode != BlendMode.NORMAL || this._methodMaterial.alphaBlending || (this._methodMaterial.colorTransform && this._methodMaterial.colorTransform.alphaMultiplier < 1));
            // effects pass is the only pass, so it should just blend normally
            this._pass.mode = MethodPassMode.SUPER_SHADER;
            this._pass.preserveAlpha = this.requiresBlending;
            this._pass.forceSeparateMVP = false;
            this._pass.colorTransform = this._methodMaterial.colorTransform;
            this._pass.shader.setBlendMode((this._methodMaterial.blendMode == BlendMode.NORMAL && this.requiresBlending) ? BlendMode.LAYER : this._methodMaterial.blendMode);
            this._pass.shader.depthCompareMode = this._methodMaterial.depthCompareMode;
        }
    };
    _Render_MethodMaterial.prototype.initCasterLightPass = function () {
        if (this._casterLightPass == null)
            this._casterLightPass = new MethodPass(MethodPassMode.LIGHTING, this, this._renderElements);
        this._casterLightPass.lightPicker = new StaticLightPicker([this._methodMaterial.shadowMethod.castingLight]);
        this._casterLightPass.shadowMethod = this._methodMaterial.shadowMethod;
        this._casterLightPass.diffuseMethod = this._methodMaterial.diffuseMethod;
        this._casterLightPass.ambientMethod = this._methodMaterial.ambientMethod;
        this._casterLightPass.normalMethod = this._methodMaterial.normalMethod;
        this._casterLightPass.specularMethod = this._methodMaterial.specularMethod;
    };
    _Render_MethodMaterial.prototype.removeCasterLightPass = function () {
        this._casterLightPass.dispose();
        this._pRemovePass(this._casterLightPass);
        this._casterLightPass = null;
    };
    _Render_MethodMaterial.prototype.initNonCasterLightPasses = function () {
        this.removeNonCasterLightPasses();
        var pass;
        var numDirLights = this._methodMaterial.lightPicker.numDirectionalLights;
        var numPointLights = this._methodMaterial.lightPicker.numPointLights;
        var numLightProbes = this._methodMaterial.lightPicker.numLightProbes;
        var dirLightOffset = 0;
        var pointLightOffset = 0;
        var probeOffset = 0;
        if (!this._casterLightPass) {
            numDirLights += this._methodMaterial.lightPicker.numCastingDirectionalLights;
            numPointLights += this._methodMaterial.lightPicker.numCastingPointLights;
        }
        this._nonCasterLightPasses = new Array();
        while (dirLightOffset < numDirLights || pointLightOffset < numPointLights || probeOffset < numLightProbes) {
            pass = new MethodPass(MethodPassMode.LIGHTING, this, this._renderElements);
            pass.includeCasters = this._methodMaterial.shadowMethod == null;
            pass.directionalLightsOffset = dirLightOffset;
            pass.pointLightsOffset = pointLightOffset;
            pass.lightProbesOffset = probeOffset;
            pass.lightPicker = this._methodMaterial.lightPicker;
            pass.diffuseMethod = this._methodMaterial.diffuseMethod;
            pass.ambientMethod = this._methodMaterial.ambientMethod;
            pass.normalMethod = this._methodMaterial.normalMethod;
            pass.specularMethod = this._methodMaterial.specularMethod;
            this._nonCasterLightPasses.push(pass);
            dirLightOffset += pass.numDirectionalLights;
            pointLightOffset += pass.numPointLights;
            probeOffset += pass.numLightProbes;
        }
    };
    _Render_MethodMaterial.prototype.removeNonCasterLightPasses = function () {
        if (!this._nonCasterLightPasses)
            return;
        for (var i = 0; i < this._nonCasterLightPasses.length; ++i)
            this._pRemovePass(this._nonCasterLightPasses[i]);
        this._nonCasterLightPasses = null;
    };
    _Render_MethodMaterial.prototype.removeEffectPass = function () {
        if (this._pass.ambientMethod != this._methodMaterial.ambientMethod)
            this._pass.ambientMethod.dispose();
        if (this._pass.diffuseMethod != this._methodMaterial.diffuseMethod)
            this._pass.diffuseMethod.dispose();
        if (this._pass.specularMethod != this._methodMaterial.specularMethod)
            this._pass.specularMethod.dispose();
        if (this._pass.normalMethod != this._methodMaterial.normalMethod)
            this._pass.normalMethod.dispose();
        this._pRemovePass(this._pass);
        this._pass = null;
    };
    _Render_MethodMaterial.prototype.initEffectPass = function () {
        if (this._pass == null)
            this._pass = new MethodPass(MethodPassMode.SUPER_SHADER, this, this._renderElements);
        if (this._methodMaterial.mode == MethodMaterialMode.SINGLE_PASS) {
            this._pass.ambientMethod = this._methodMaterial.ambientMethod;
            this._pass.diffuseMethod = this._methodMaterial.diffuseMethod;
            this._pass.specularMethod = this._methodMaterial.specularMethod;
            this._pass.normalMethod = this._methodMaterial.normalMethod;
            this._pass.shadowMethod = this._methodMaterial.shadowMethod;
        }
        else if (this._methodMaterial.mode == MethodMaterialMode.MULTI_PASS) {
            if (this.numLights == 0) {
                this._pass.ambientMethod = this._methodMaterial.ambientMethod;
            }
            else {
                this._pass.ambientMethod = null;
            }
            this._pass.preserveAlpha = false;
            this._pass.normalMethod = this._methodMaterial.normalMethod;
        }
        //update effect methods
        var i = 0;
        var effectMethod;
        var len = Math.max(this._methodMaterial.numEffectMethods, this._pass.numEffectMethods);
        while (i < len) {
            effectMethod = this._methodMaterial.getEffectMethodAt(i);
            if (effectMethod != this._pass.getEffectMethodAt(i)) {
                this._pass.removeEffectMethodAt(i);
                if (effectMethod != null) {
                    if (i < this._pass.numEffectMethods)
                        this._pass.addEffectMethodAt(effectMethod, i);
                    else
                        this._pass.addEffectMethod(effectMethod);
                }
            }
            i++;
        }
    };
    /**
     * @inheritDoc
     */
    _Render_MethodMaterial.prototype.onClear = function (event) {
        _super.prototype.onClear.call(this, event);
        //TODO
    };
    return _Render_MethodMaterial;
}(_Render_MaterialBase));
export { _Render_MethodMaterial };
CacheRenderer.registerMaterial(_Render_MethodMaterial, MethodMaterial);
DefaultRenderer.registerMaterial(_Render_MethodMaterial, MethodMaterial);
DepthRenderer.registerMaterial(_Render_DepthMaterial, MethodMaterial);
DistanceRenderer.registerMaterial(_Render_DistanceMaterial, MethodMaterial);
